import { useEffect, useMemo, useState } from "react";
import { useCentralStore } from "../../../../store/Central";
import { useUserManagementStore } from "../../../../store/UserManagement";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import { TooltipWrapper } from "../../../../components/ui/tooltip-wrapper";
import {
	RadioGroup,
	RadioGroupItem,
} from "../../../../components/ui/radio-group";
import {
	EditIcon,
	InfoIcon,
	Play,
	FileText,
	CreditCard,
	Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Logger } from "@/lib/logger/Logger";
import { Member } from "../../../../store/UserManagement/user-management.store";
import { RolesEnum } from "@/lib/types/roles.types";
import { showNotification } from "../../../../store/Central/selectors";
import { PaymentMethodType } from "@/lib/types/stripe.types";
import { CALENDLY_DEMO_URL } from "@/lib/constants/urls";

// Make sure to call `loadStripe` outside of a component's render to avoid
// recreating the `Stripe` object on every render.

const SubscriptionStatus: Record<string, string> = {
	active: "Aktiv",
	trialing: "Testzeitraum",
	past_due: "Überfällig",
	incomplete: "Unvollständig",
	incomplete_expired: "Unvollständig und abgelaufen",
	unpaid: "Unbezahlt",
	canceled: "Storniert",
};

const VAT = 0.081; // 8.1%

export const AbonnementPage: React.FC = () => {
	const navigate = useNavigate();
	const subscription = useCentralStore((state) => state.subscription);
	const getSubscription = useCentralStore((state) => state.getSubscription);
	const getOrganization = useCentralStore((state) => state.getOrganization);
	const subscriptionTier = useCentralStore((state) => state.subscriptionTier);
	const organization = useCentralStore((state) => state.organization);
	const [isDentalLabXpUser, setIsDentalLabXpUser] = useState(false);
	const [owner, setOwner] = useState<Member | null>(null);
	const [paymentMethod, setPaymentMethod] =
		useState<PaymentMethodType>("card");
	const [isLoading, setIsLoading] = useState(false);
	const [invoiceAddress, setInvoiceAddress] = useState({
		street: "",
		postalCode: "",
		city: "",
		vatNumber: "",
	});

	const getActiveMembers = useUserManagementStore(
		(state) => state.getActiveMembers
	);
	const getMembers = useUserManagementStore((state) => state.getMembers);
	const activeUsers = useMemo(
		() => getActiveMembers(new Date()),
		[getActiveMembers]
	);

	const role = useCentralStore((state) => state.role);

	useEffect(() => {
		if (organization) {
			setInvoiceAddress({
				street: organization.street ?? "",
				postalCode: organization.postal_code ?? "",
				city: organization.city ?? "",
				vatNumber: organization.mwst_number ?? "",
			});
		}
	}, [organization]);

	const BASE_LICENSE_PRICE = 840;
	const USER_PRICE = 120;

	const {
		quantity,
		yearlyPayment,
		yearlyPaymentWithVAT,
		yearlyPaymentWithDiscount,
	} = useMemo(() => {
		const qty =
			subscription?.status === "active"
				? subscription.quantity ?? activeUsers.length
				: activeUsers.length;
		const applyDiscount =
			subscription?.status !== "active" && isDentalLabXpUser;

		const yearlyDue = BASE_LICENSE_PRICE + qty * USER_PRICE;
		return {
			quantity: qty,
			yearlyPayment: yearlyDue,
			yearlyPaymentWithVAT: yearlyDue * (1 + VAT),
			yearlyPaymentWithDiscount:
				(applyDiscount ? yearlyDue * 0.75 : yearlyDue) * (1 + VAT),
		};
	}, [subscription, activeUsers.length, isDentalLabXpUser]);

	useEffect(() => {
		const ownerData = activeUsers.find((user) => user.role == "Besitzer");

		if (ownerData) {
			setOwner(ownerData);
		}
	}, [activeUsers]);

	useEffect(() => {
		getMembers();
		getSubscription();
	}, []);

	const handleManageBilling = async () => {
		if (subscription?.customer_id) {
			try {
				const response = await fetch(
					`${
						import.meta.env.VITE_PDF_MICROSERVICE_URL
					}/stripe/create-customer-portal-session`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							customer_id: subscription?.customer_id,
						}),
					}
				);

				const data = await response.json();
				Logger.log("Antwort", data);

				if (response.ok) {
					window.location.href = data.url;
				} else {
					showNotification({
						message:
							"Fehler beim Zugriff auf das Kundenportal. Melden Sie sich bitte beim Support.",
						type: "error",
					});
				}
			} catch (error) {
				showNotification({
					message:
						"Fehler beim Zugriff auf das Kundenportal. Melden Sie sich bitte beim Support.",
					type: "error",
				});
			}
		}
	};

	const handlePayment = async () => {
		if (!organization || !owner) return;

		setIsLoading(true);

		const trialPeriod = Math.ceil(
			Math.max(
				(new Date(subscription?.trial_period_end ?? "").getTime() -
					new Date().getTime()) /
					(1000 * 60 * 60 * 24),
				0
			)
		);

		let customerId = subscription?.customer_id;

		try {
			const customerData =
				paymentMethod === "invoice"
					? {
							email: owner.email,
							organization_name: organization.name,
							street_and_number: invoiceAddress.street,
							postal_code: invoiceAddress.postalCode,
							city: invoiceAddress.city,
							country: organization.country,
							organization_id: organization.id,
							vat_number: invoiceAddress.vatNumber || undefined,
						}
					: {
							email: owner.email,
							organization_name: organization.name,
							street_and_number: organization.street,
							postal_code: organization.postal_code,
							city: organization.city,
							country: organization.country,
							organization_id: organization.id,
						};

			if (!customerId) {
				const response = await fetch(
					`${
						import.meta.env.VITE_PDF_MICROSERVICE_URL
					}/stripe/create-customer`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify(customerData),
					}
				);

				if (!response.ok) {
					showNotification({
						message:
							"Fehler beim Erstellen des Kundenprofils. Bitte versuchen Sie es später erneut.",
						type: "error",
					});
					throw new Error(
						`Failed to create customer: ${response.statusText}`
					);
				}

				const data = await response.json();
				customerId = data.customer_id;
			}

			const paymentData = {
				amount_of_users: activeUsers.length,
				customer_id: customerId,
				organization_id: organization.id,
				discount: isDentalLabXpUser,
				trial_period: trialPeriod > 0 ? trialPeriod : 0,
			};

			if (paymentMethod === "invoice") {
				Logger.log(
					"Creating invoice subscription directly",
					paymentData
				);

				const response = await fetch(
					`${
						import.meta.env.VITE_PDF_MICROSERVICE_URL
					}/stripe/create-invoice-subscription`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify(paymentData),
					}
				);

				if (!response.ok) {
					Logger.error(
						response,
						{},
						"Error creating invoice subscription"
					);
					throw new Error(
						`Failed to create subscription: ${response.statusText}`
					);
				}

				await new Promise((resolve) => setTimeout(resolve, 1500));
				await getOrganization();
				await getSubscription();
				showNotification({
					message: "Abonnement erfolgreich erstellt.",
					type: "success",
				});
			} else {
				Logger.log("Creating card payment session", paymentData);

				const response = await fetch(
					`${
						import.meta.env.VITE_PDF_MICROSERVICE_URL
					}/stripe/create-subscription-session`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify(paymentData),
					}
				);

				if (!response.ok) {
					Logger.error(
						response,
						{},
						"Error creating payment session"
					);
					throw new Error(
						`Failed to create session link: ${response.statusText}`
					);
				}

				const data = await response.json();

				if (data?.sessionLink) {
					window.location.href = data.sessionLink;
				} else {
					Logger.error("No session link returned from API");
				}
			}
		} catch (error) {
			Logger.error(error, {}, "Error creating payment session");
			showNotification({
				message:
					"Fehler beim Erstellen der Zahlungssitzung. Melden Sie sich bitte beim Support.",
				type: "error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	if (role !== RolesEnum.OWNER) {
		return (
			<div className="p-6">
				<div className="max-w-6xl">
					<h1 className="text-2xl font-semibold text-gray-900 mb-6">
						Abonnement
					</h1>
					<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
						<p className="text-gray-600">
							Sie haben keine Berechtigung auf diese Seite
							zuzugreifen.
						</p>
					</div>
				</div>
			</div>
		);
	}

	if (
		subscription &&
		(subscription.status == "active" ||
			(subscription.status == "trialing" &&
				subscription.subscription_id !== null) ||
			subscription.status == "past_due")
	) {
		return (
			<div className="p-6">
				<div className="max-w-6xl">
					<h1 className="text-2xl font-semibold text-gray-900 mb-6">
						Abonnement
					</h1>
					<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
						<div className="space-y-4">
							<div className="grid grid-cols-2 gap-20 mb-10">
								<div>
									<h2 className="text-gray-600 text-lg">
										Status
									</h2>
									<p className="text-xl font-semibold text-gray-800">
										{
											SubscriptionStatus[
												subscription.status
											]
										}
									</p>
								</div>
								<div>
									<h2 className="text-gray-600 text-lg">
										Anzahl bezahlter Nutzer
									</h2>
									<p className="text-xl font-semibold text-gray-800 flex items-center gap-2">
										{subscription.quantity}
										<Button
											variant="ghost"
											size="icon"
											aria-label="edit"
											className="ml-2 h-8 w-8 text-gray-600 hover:text-gray-900"
											onClick={() =>
												navigate("/user-management")
											}
										>
											<EditIcon className="h-4 w-4" />
										</Button>
									</p>
								</div>
								{subscription.status === "trialing" ? (
									<div>
										<h2 className="text-gray-600 text-lg">
											Testzeitraum Ende
										</h2>
										<p className="text-xl font-semibold text-gray-800">
											{subscription.period_end}
										</p>
									</div>
								) : (
									<>
										<div>
											<h2 className="text-gray-600 text-lg">
												Start
											</h2>
											<p className="text-xl font-semibold text-gray-800">
												{subscription.period_start}
											</p>
										</div>
										<div>
											<h2 className="text-gray-600 text-lg">
												Ende
											</h2>
											<p className="text-xl font-semibold text-gray-800">
												{subscription.period_end}
											</p>
										</div>
									</>
								)}
								<div>
									<h2 className="text-gray-600 text-lg flex items-center">
										Jährliche Zahlung
										<TooltipWrapper title="Dieser Wert kann von der tatsächlichen Summe abweichen. Es gilt der Wert aus dem unten verlinkten Kundenportal.">
											<InfoIcon
												size={16}
												className="ml-2 text-gray-600 hover:text-gray-900 cursor-help"
											/>
										</TooltipWrapper>
									</h2>
									<p className="text-xl font-semibold text-gray-800">
										CHF {yearlyPaymentWithVAT.toFixed(2)}
									</p>
								</div>
								<div>
									<h2 className="text-gray-600 text-lg">
										Automatische Verlängerung
									</h2>
									<p className="text-xl font-semibold text-gray-800">
										{subscription.cancel_at_period_end
											? "Nein"
											: "Ja"}
									</p>
								</div>
								{subscription.payment_method_type ===
									"invoice" && (
									<div>
										<h2 className="text-gray-600 text-lg flex items-center gap-2">
											<FileText className="h-4 w-4" />
											Zahlungsmethode
										</h2>
										<p className="text-xl font-semibold text-gray-800">
											Rechnungszahlung
										</p>
									</div>
								)}
							</div>
							{subscription.payment_method_type === "invoice" &&
								subscription.status === "trialing" && (
									<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
										<p className="text-sm text-blue-800">
											Nach Ablauf des Testzeitraums
											erhalten Sie die Rechnung per
											E-Mail. Diese ist innerhalb von 14
											Tagen zu begleichen.
										</p>
									</div>
								)}
							<button
								onClick={handleManageBilling}
								className="mt-6 px-4 py-2 bg-[#003261] text-white rounded hover:bg-[#00284e] transition-colors"
							>
								Zur Verwaltung Ihres Abonnements
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (subscriptionTier === "eligible") {
		return (
			<div className="p-6">
				<div
					className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4"
					data-testid="start-trial-section"
				>
					<div className="flex items-start gap-4">
						<div className="bg-green-100 rounded-full p-3">
							<Play className="h-6 w-6 text-green-600" />
						</div>
						<div className="flex-1">
							<h2 className="text-xl font-semibold text-green-900 mb-2">
								30 Tage kostenlos testen
							</h2>
							<p className="text-green-800 mb-4">
								Buchen Sie eine kostenlose Demo und erhalten Sie
								Ihren Aktivierungscode für 30 Tage kostenloses
								Testen.
							</p>
							<Button
								onClick={() =>
									window.open(CALENDLY_DEMO_URL, "_blank")
								}
								className="bg-green-600 hover:bg-green-700 text-white"
								aria-label="start-trial-button"
								data-testid="start-trial-button"
							>
								Jetzt starten
							</Button>
						</div>
					</div>
				</div>
			</div>
		);
	}
	if (isLoading) {
		return (
			<div className="p-6">
				<div className="max-w-6xl">
					<h1 className="text-2xl font-semibold text-gray-900 mb-6">
						Abonnement
					</h1>
					<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
						<div className="flex flex-col items-center justify-center py-12">
							<Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
							{paymentMethod === "invoice" ? (
								<p className="text-gray-600">
									Abonnement wird erstellt...
								</p>
							) : (
								<p className="text-gray-600">
									Weiterleitung zur Zahlung...
								</p>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="max-w-6xl">
				<h1 className="text-2xl font-semibold text-gray-900 mb-6">
					Abonnement
				</h1>
				<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
					<div className="space-y-3">
						<div>
							<div className="flex items-center justify-between mb-4">
								<h2 className="text-xl font-semibold">
									Techniker ({activeUsers.length})
								</h2>
								<TooltipWrapper title="Nutzer verwalten">
									<Button
										variant="ghost"
										size="icon"
										className="h-8 w-8"
										onClick={() =>
											navigate("/user-management")
										}
									>
										<EditIcon className="h-4 w-4" />
									</Button>
								</TooltipWrapper>
							</div>
							<ul className="divide-y">
								{activeUsers.map((user) => (
									<li key={user.id} className="py-2">
										<span className="text-gray-700">
											{`${user.first_name || ""} ${
												user.last_name || ""
											}`.trim() ? (
												`${user.first_name || ""} ${
													user.last_name || ""
												}`.trim()
											) : (
												<span>
													{user.email}{" "}
													<span className="text-gray-500">
														(eingeladen)
													</span>
												</span>
											)}
										</span>
									</li>
								))}
							</ul>
						</div>

						{(!subscription || !subscription.existing_invoices) && (
							<div className="flex items-center gap-2">
								<Checkbox
									id="dental-lab-xp"
									checked={isDentalLabXpUser}
									onCheckedChange={(checked) =>
										setIsDentalLabXpUser(checked === true)
									}
								/>
								<Label htmlFor="dental-lab-xp">
									Dental lab XP Nutzer *
								</Label>
							</div>
						)}

						<div className="bg-gray-50 p-4 rounded-lg space-y-3">
							<div className="space-y-1">
								<div className="flex justify-between items-center">
									<span>Basislizenz</span>
									<span>
										CHF {BASE_LICENSE_PRICE.toFixed(2)}
									</span>
								</div>
								<div className="flex justify-between items-center">
									<span>
										{quantity}{" "}
										{quantity === 1 ? "Nutzer" : "Nutzer"} ×
										CHF {USER_PRICE}
									</span>
									<span>
										CHF {(quantity * USER_PRICE).toFixed(2)}
									</span>
								</div>
								<div className="flex justify-between items-center font-medium pt-1 border-t border-gray-200">
									<span>Jährlicher Preis</span>
									<span>CHF {yearlyPayment.toFixed(2)}</span>
								</div>
								<div className="flex justify-between items-center text-sm text-gray-600">
									<span>Monatlich</span>
									<span>
										CHF {(yearlyPayment / 12).toFixed(2)}
									</span>
								</div>
							</div>
							{isDentalLabXpUser && (
								<>
									<div className="flex text-green-600 justify-between items-center">
										<span>Gutschrift</span>
										<span>
											- CHF{" "}
											{(yearlyPayment * 0.25).toFixed(2)}
										</span>
									</div>
									<div className="border-t border-gray-200" />
									<div className="flex justify-between items-center">
										<span>Subtotal</span>
										<span>
											CHF{" "}
											{(yearlyPayment * 0.75).toFixed(2)}
										</span>
									</div>
								</>
							)}
							<div className="flex justify-between items-center">
								<span>MwSt. (8.1%)</span>
								<span>
									CHF{" "}
									{(
										yearlyPayment *
										(isDentalLabXpUser ? 0.75 : 1) *
										VAT
									).toFixed(2)}
								</span>
							</div>
							<div>
								<div className="flex justify-between items-center font-semibold pt-2 border-t">
									<span>Gesamt</span>
									<div className="text-right">
										<div>
											CHF{" "}
											{yearlyPaymentWithDiscount.toFixed(
												2
											)}
										</div>
									</div>
								</div>
								<div className="flex justify-between items-center text-sm text-gray-600">
									<span>Monatlich</span>
									<span>
										CHF{" "}
										{(
											yearlyPaymentWithDiscount / 12
										).toFixed(2)}
									</span>
								</div>
							</div>
						</div>

						<div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
							<h3 className="font-semibold text-gray-900">
								Vertragsdetails
							</h3>
							<div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700">
								<span>Vertragslaufzeit:</span>
								<span>1 Jahr (Jahreslizenz)</span>
								<span>Automatische Verlängerung:</span>
								<span>Ja, jährlich zum aktuellen Preis</span>
								<span>Kündigung:</span>
								<span>Jederzeit über das Kundenportal</span>
								<span>Zahlungsfrist:</span>
								<span>14 Tage (bei Rechnungszahlung)</span>
							</div>
							<p className="text-gray-600 pt-2">
								Mit dem Abschluss akzeptieren Sie unsere{" "}
								<a
									href="https://dentdesk.ch/agb"
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-600 hover:underline"
								>
									AGB
								</a>
								.
							</p>
							<div className="text-xs text-gray-500 pt-2 border-t border-gray-200 mt-2">
								dentDESK GmbH · Waldheimstrasse 48 · CH-3012
								Bern · CHE-191.992.704 MwSt
							</div>
						</div>

						<div className="space-y-4 pt-4">
							<div className="space-y-3">
								<Label className="text-sm font-medium">
									Zahlungsmethode
								</Label>
								<RadioGroup
									value={paymentMethod}
									onValueChange={(value) =>
										setPaymentMethod(
											value as PaymentMethodType
										)
									}
									className="grid gap-3"
									aria-label="payment-method-selection"
									data-testid="payment-method-selection"
								>
									<div
										className="flex items-center space-x-3 border rounded-lg p-4 cursor-pointer hover:bg-gray-50"
										onClick={() => setPaymentMethod("card")}
									>
										<RadioGroupItem
											value="card"
											id="card"
											data-testid="payment-method-card"
										/>
										<CreditCard className="h-5 w-5 text-gray-600" />
										<Label
											htmlFor="card"
											className="flex-1 cursor-pointer"
										>
											<span className="font-medium">
												Kreditkarte
											</span>
											<p className="text-sm text-gray-500">
												Sofortige Zahlung per Karte
											</p>
										</Label>
									</div>

									<div
										className="flex items-center space-x-3 border rounded-lg p-4 cursor-pointer hover:bg-gray-50"
										onClick={() =>
											setPaymentMethod("invoice")
										}
									>
										<RadioGroupItem
											value="invoice"
											id="invoice"
											data-testid="payment-method-invoice"
										/>
										<FileText className="h-5 w-5 text-gray-600" />
										<Label
											htmlFor="invoice"
											className="flex-1 cursor-pointer"
										>
											<span className="font-medium">
												Rechnungszahlung
											</span>
											<p className="text-sm text-gray-500">
												Zahlung innert 14 Tagen per
												Rechnung
											</p>
										</Label>
									</div>
								</RadioGroup>
							</div>

							{paymentMethod === "invoice" && (
								<div className="space-y-3 p-4 border rounded-lg bg-gray-50">
									<h4 className="font-medium text-sm">
										Rechnungsadresse
									</h4>
									<div className="grid gap-3">
										<div>
											<Label
												htmlFor="street"
												className="text-sm"
											>
												Strasse und Hausnummer
											</Label>
											<Input
												id="street"
												value={invoiceAddress.street}
												onChange={(e) =>
													setInvoiceAddress(
														(prev) => ({
															...prev,
															street: e.target
																.value,
														})
													)
												}
												placeholder="Musterstrasse 1"
											/>
										</div>
										<div className="grid grid-cols-2 gap-3">
											<div>
												<Label
													htmlFor="postalCode"
													className="text-sm"
												>
													PLZ
												</Label>
												<Input
													id="postalCode"
													value={
														invoiceAddress.postalCode
													}
													onChange={(e) =>
														setInvoiceAddress(
															(prev) => ({
																...prev,
																postalCode:
																	e.target
																		.value,
															})
														)
													}
													placeholder="8000"
												/>
											</div>
											<div>
												<Label
													htmlFor="city"
													className="text-sm"
												>
													Ort
												</Label>
												<Input
													id="city"
													value={invoiceAddress.city}
													onChange={(e) =>
														setInvoiceAddress(
															(prev) => ({
																...prev,
																city: e.target
																	.value,
															})
														)
													}
													placeholder="Zürich"
												/>
											</div>
										</div>
										<div>
											<Label
												htmlFor="vatNumber"
												className="text-sm"
											>
												MwSt-Nummer (optional)
											</Label>
											<Input
												id="vatNumber"
												value={invoiceAddress.vatNumber}
												onChange={(e) =>
													setInvoiceAddress(
														(prev) => ({
															...prev,
															vatNumber:
																e.target.value,
														})
													)
												}
												placeholder="CHE-123.456.789 MWST"
											/>
										</div>
									</div>
								</div>
							)}

							<div className="flex gap-4">
								<Button
									onClick={handlePayment}
									disabled={isLoading}
									aria-label="proceed-to-payment"
									data-testid="proceed-to-payment-button"
								>
									{isLoading
										? "Wird geladen..."
										: paymentMethod === "card"
											? "Zur Zahlungsseite"
											: "Jetzt kostenpflichtig abonnieren"}
								</Button>

								{subscription?.existing_invoices && (
									<Button
										variant="outline"
										onClick={handleManageBilling}
									>
										Zum Kundenportal
									</Button>
								)}
							</div>
						</div>

						{subscription && subscription.existing_invoices ? (
							<p className="text-sm text-gray-500">
								Es gelten die Preise der Zahlungsseite, dort
								werden auch vergangene Gutschriften
								berücksichtigt.
							</p>
						) : (
							<div className="flex flex-row gap-2">
								<span className="text-sm">*</span>
								<p className="text-sm text-gray-500">
									Sollten Sie noch ein bestehendes
									Support-Abonnement für Dental lab XP haben,
									kündigen Sie dieses bitte bei der YSD AG, um
									Doppelbelastungen zu vermeiden.
								</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
